import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  parseTelegramPeerReferenceOrThrow,
  TelegramPeerReference,
  TelegramPeerReferenceError,
} from '@synapex/shared2/telegram';
import { isAxiosError } from 'axios';

//schema
export const schema = z.object({
  telegram_peer_ref: z.string().min(1, { message: 'Введите канал' }),
});

export type FormValues = z.input<typeof schema>;

//values
export const DEFAULT_FORM_VALUES: FormValues = {
  telegram_peer_ref: '',
};

const useAwaitChannelForm = () =>
  useForm({
    resolver: zodResolver(schema),

    defaultValues: DEFAULT_FORM_VALUES,
  });

const getTelegramPeerRefErrorMessage = (error: unknown) => {
  if (error instanceof TelegramPeerReferenceError) {
    return {
      NEITHER_URL_NOR_USERNAME:
        'Нужно ввести @channel или t.me/channel, или инвайт-ссылку',
      USERNAME_INVALID: 'Неверный username',
    }[error.key];
  } else {
    return 'Не удалось добавить канал';
  }
};

const getMutationErrorMessage = (error: Error) => {
  if (isAxiosError(error)) {
    const errorBody: unknown = error.response?.data;

    if (errorBody && typeof errorBody === 'object') {
      if ('registered' in errorBody && errorBody.registered) {
        return 'Этот канал уже добавлен';
      } else if (
        'status' in errorBody &&
        typeof errorBody.status === 'string'
      ) {
        switch (errorBody.status) {
          case 'USERNAME_INVALID': {
            return 'Неверный username';
          }
          case 'USERNAME_NOT_FOUND': {
            return 'Канал с таким username не найден';
          }
          case 'USERNAME_PEER_NOT_CHANNEL': {
            if (
              'inputPeerClass' in errorBody &&
              errorBody.inputPeerClass === 'InputPeerUser'
            ) {
              return 'Это пользователь, а нужно ввести канал';
            } else {
              return 'Неправильный канал';
            }
          }
          case 'HASH_CHECK_CHAT_INVITE_ERROR': {
            return 'Неверная инвайт-ссылка';
          }
          default: {
            return errorBody.status;
          }
        }
      }
    }
  }

  return 'Не удалось добавить канал';
};

export type UsePutChannelRequestArgs = {
  mutateAsync: (args: {
    channel: TelegramPeerReference;
    onError: (error: Error) => void;
  }) => Promise<unknown>;
};
export const usePutChannelRequest = ({
  mutateAsync,
}: UsePutChannelRequestArgs) => {
  const { formState, handleSubmit, control, setError } = useAwaitChannelForm();

  const onSubmit = handleSubmit((values) => {
    let channel: TelegramPeerReference;
    try {
      channel = parseTelegramPeerReferenceOrThrow(values.telegram_peer_ref);
    } catch (error: unknown) {
      console.log(error);
      setError('telegram_peer_ref', {
        message: getTelegramPeerRefErrorMessage(error),
      });
      return;
    }

    return mutateAsync({
      channel,
      onError(error) {
        console.log(error);
        setError('telegram_peer_ref', {
          message: getMutationErrorMessage(error),
        });
      },
    });
  });

  const isSubmitting = formState.isSubmitting;

  return {
    onSubmit,
    isSubmitting,
    control,
    error: formState.errors.root?.message,
  };
};
